// Condición de las piezas que regresan al cancelar una venta: qué se pregunta, cómo se valida y
// qué viaja al silo. Solo viaja lo dañado; lo no listado vuelve en buen estado.
import { z } from 'zod';
import type { ReturnPieceCondition } from '@/api/services/retail-wizard/retail-wizard.client';
import type { SaleLine } from '@/api/services/retail-wizard/contract';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';

/** Tope del motivo de la cancelación y del de cada daño; es el mismo que valida el silo. */
export const CANCEL_REASON_MAX = 200;

/** Mínimo del motivo de la cancelación; el silo rechaza con 400 uno más corto. */
export const CANCEL_REASON_MIN = 10;

/** Pieza vendida con IMEI o serie: se devuelve una por una. */
export interface ReturnPieceRow {
    sku: string;
    identifier: string;
    label: string;
    /** Línea de regalo de la que sale; ausente si se vendió. */
    giftLine?: SaleLine;
}

/** SKU vendido por cantidad: se dice cuántas unidades vienen dañadas. */
export interface ReturnQuantityRow {
    sku: string;
    label: string;
    soldQty: number;
    /** Línea de regalo de la que salen todas sus unidades; ausente si alguna se vendió. */
    giftLine?: SaleLine;
}

export interface ReturnablePieces {
    pieces: ReturnPieceRow[];
    quantities: ReturnQuantityRow[];
}

function productName(line: SaleLine): string {
    return itemDisplayName({ sku: line.sku, ...line.product });
}

/** Nombre de un componente: el de la línea si es ella misma, o su SKU con el kit que lo trajo. */
function componentLabel(line: SaleLine, sku: string, t: Translate): string {
    if (sku === line.sku) return productName(line);
    return t('cancelar.componenteDelKit', {
        defaultValue: '{{sku}} · del kit {{kit}}',
        sku,
        kit: productName(line),
    });
}

const byLabel = (a: { label: string }, b: { label: string }): number =>
    a.label.localeCompare(b.label, 'es-MX');

/** Piezas por identificador y SKUs por cantidad de la venta; un servicio no regresa a inventario. */
export function returnablePiecesOf(lines: SaleLine[] | undefined, t: Translate): ReturnablePieces {
    const pieces: ReturnPieceRow[] = [];
    const quantities = new Map<string, ReturnQuantityRow>();
    for (const line of (lines ?? []).filter((l) => l.product?.itemType !== 'SERVICE')) {
        const gift = line.role === 'GIFT' ? { giftLine: line } : {};
        for (const component of line.components) {
            if (component.identifiers.length > 0) {
                for (const identifier of component.identifiers) {
                    pieces.push({
                        sku: component.sku,
                        identifier,
                        label: componentLabel(line, component.sku, t),
                        ...gift,
                    });
                }
                continue;
            }
            const row = quantities.get(component.sku);
            if (row) {
                row.soldQty += component.qty;
                if (line.role !== 'GIFT') delete row.giftLine;
            } else {
                quantities.set(component.sku, {
                    sku: component.sku,
                    label: componentLabel(line, component.sku, t),
                    soldQty: component.qty,
                    ...gift,
                });
            }
        }
    }
    return {
        pieces: pieces.sort((a, b) => byLabel(a, b) || a.identifier.localeCompare(b.identifier)),
        quantities: [...quantities.values()].sort(byLabel),
    };
}

/** Traductor del namespace `ventas`: `defaultValue` es el respaldo y el resto, sus variables. */
export type Translate = (
    key: string,
    options: { defaultValue: string } & Record<string, string | number>,
) => string;

const conditionSchema = z.enum(['GOOD', 'DAMAGED']);

/** Esquema del formulario de cancelación; sin teléfono, la casilla confirma las piezas. */
export function buildCancelSaleFormSchema(t: Translate, { withoutPhone = false } = {}) {
    const pieceSchema = z.object({
        sku: z.string(),
        identifier: z.string(),
        label: z.string(),
        condition: conditionSchema,
        damageReason: z.string().max(CANCEL_REASON_MAX),
    });

    const quantitySchema = z.object({
        sku: z.string(),
        label: z.string(),
        soldQty: z.number().int(),
        damagedQty: z
            .number()
            .int(t('cancelar.validacion.entero', { defaultValue: 'Escribe un número entero.' }))
            .min(0, t('cancelar.validacion.negativo', { defaultValue: 'No puede ser negativo.' })),
        damageReason: z.string().max(CANCEL_REASON_MAX),
    });

    return z
        .object({
            reason: z
                .string()
                .trim()
                .min(
                    1,
                    t('cancelar.validacion.motivoRequerido', {
                        defaultValue: 'Escribe el motivo.',
                    }),
                )
                .min(
                    CANCEL_REASON_MIN,
                    t('cancelar.validacion.motivoCorto', {
                        defaultValue: 'Escribe al menos {{min}} caracteres.',
                        min: CANCEL_REASON_MIN,
                    }),
                )
                .max(CANCEL_REASON_MAX),
            equipmentVerified: z.boolean().refine(
                (v) => v,
                withoutPhone
                    ? t('cancelar.validacion.piezasSinConfirmar', {
                          defaultValue: 'Confirma que revisaste las piezas que se devuelven.',
                      })
                    : t('cancelar.validacion.equipoSinVerificar', {
                          defaultValue: 'Confirma que verificaste el equipo.',
                      }),
            ),
            pieces: z.array(pieceSchema),
            quantities: z.array(quantitySchema),
        })
        .superRefine((form, ctx) => {
            form.pieces.forEach((piece, i) => {
                if (piece.condition === 'DAMAGED' && piece.damageReason.trim() === '') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['pieces', i, 'damageReason'],
                        message: t('cancelar.validacion.motivoDanoPieza', {
                            defaultValue: 'Escribe por qué viene dañada.',
                        }),
                    });
                }
            });
            form.quantities.forEach((row, i) => {
                if (row.damagedQty > row.soldQty) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['quantities', i, 'damagedQty'],
                        message: t('cancelar.validacion.masDanadasQueVendidas', {
                            defaultValue: 'No pueden ser más de las {{soldQty}} vendidas.',
                            soldQty: row.soldQty,
                        }),
                    });
                }
                if (row.damagedQty > 0 && row.damageReason.trim() === '') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['quantities', i, 'damageReason'],
                        message: t('cancelar.validacion.motivoDanoUnidades', {
                            defaultValue: 'Escribe por qué vienen dañadas.',
                        }),
                    });
                }
            });
        });
}

export type CancelSaleForm = z.infer<ReturnType<typeof buildCancelSaleFormSchema>>;

/** Formulario vacío: todo en buen estado y ninguna unidad dañada. */
export function emptyCancelSaleForm({ pieces, quantities }: ReturnablePieces): CancelSaleForm {
    return {
        reason: '',
        equipmentVerified: false,
        pieces: pieces.map(({ sku, identifier, label }) => ({
            sku,
            identifier,
            label,
            condition: 'GOOD',
            damageReason: '',
        })),
        quantities: quantities.map(({ sku, label, soldQty }) => ({
            sku,
            label,
            soldQty,
            damagedQty: 0,
            damageReason: '',
        })),
    };
}

/** Lo que viaja en `returnConditions`: solo las piezas y unidades dañadas. */
export function toReturnConditions(form: CancelSaleForm): ReturnPieceCondition[] {
    const pieces = form.pieces
        .filter((p) => p.condition === 'DAMAGED')
        .map(
            (p): ReturnPieceCondition => ({
                sku: p.sku,
                identifier: p.identifier,
                condition: 'DAMAGED',
                reason: p.damageReason.trim(),
            }),
        );
    const quantities = form.quantities
        .filter((q) => q.damagedQty > 0)
        .map(
            (q): ReturnPieceCondition => ({
                sku: q.sku,
                qty: q.damagedQty,
                condition: 'DAMAGED',
                reason: q.damageReason.trim(),
            }),
        );
    return [...pieces, ...quantities];
}
